import { useState, useMemo } from "react"
import { useStateWithCallbackLazy } from "use-state-with-callback"
import { useShallow } from "zustand/react/shallow"

// components
import Modal from "components/Modal/Modal"
import { PrimeButton } from "components-design-system"
import AdverseEventsXMLImport from "./subcomponents/AdverseEventsXMLImport/AdverseEventsXMLImport"
import AdverseProperties from "./subcomponents/AdverseProperties/AdverseProperties"
import Arms from "./subcomponents/Arms/Arms"
import CTGArmTotal from "./subcomponents/CTGArmTotal/CTGArmTotal"
import EventList from "./subcomponents/EventList/EventList"
import CellValidation from "../components/CellValidation/CellValidation"

// context
import useResultsTableViewStore from "../../../hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "../../../hooks/stores/useAuthoringViewStore"
import useAuthoringStore from "../../../hooks/stores/useAuthoringStore"

// api
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"

import { usePostAeManualImport } from "api/hooks/etl/aeImportApi"

// utils
import jsonToFormData from "utilities/jsonToFormData"

const RJAdverseEvents = (props) => {
  const { schema, adverseData, setAdverseData } = props

  const { authoringId, setIsUserLocked, setLockedFullName } = useAuthoringStore(
    useShallow((state) => ({
      authoringId: state.authoringId,
      setIsUserLocked: state.setIsUserLocked,
      setLockedFullName: state.setLockedFullName,
    }))
  )

  const { readOnly, setReadOnly, formView, customTableWidthClassName } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      setReadOnly: state.setReadOnly,
      formView: state.formView,
      customTableWidthClassName: state.customTableWidthClassName,
    }))
  )

  const { tableView } = useResultsTableViewStore()

  const { data: authoring } = useGetAuthoringStudy(authoringId)
  const postAeManualImport = usePostAeManualImport(authoringId)
  const [aeFile, setAeFile] = useStateWithCallbackLazy(null)
  const [openAeImportModal, setOpenAeImportModal] = useState(false)
  const [showAeImportErrors, setShowAeImportErrors] = useState(false)
  const [aeImportError, setAeImportError] = useState("")
  const [showImportSuccess, setShowImportSuccess] = useState(false)
  const [loadingXmlImport, setLoadingXmlImport] = useState(false)

  const handleFileload = (file) => {
    if (file?.name) {
      if (file.name.slice(file.name.length - 4).toLowerCase() !== ".xml") {
        setShowAeImportErrors(true)
        setAeImportError("Invalid file type, please upload an XML file.")
        return null
      }
    }
    setAeFile(file)
  }

  const handleImport = async () => {
    setLoadingXmlImport(true)
    // TODO: make request
    // if success
    let formData = jsonToFormData({
      xml_file: aeFile,
      study_id: authoring.study_id,
      file_type: tableView,
    })

    postAeManualImport(
      { body: formData },
      {
        onSuccess: (res) => {
          if (res.status >= 300) {
            setShowAeImportErrors(true)
            setAeImportError(res.data?.error_msg)
          } else {
            setShowImportSuccess(true)
            setOpenAeImportModal(false)
            setReadOnly(true)
          }
        },
        onError: (err) => {
          if (err?.response?.data?.error?.msg) {
            let errorMsg = err.response.data.error.msg
            setShowAeImportErrors(true)
            setAeImportError(errorMsg)
          }
        },
        onSettled: () => {
          setLoadingXmlImport(false)
        },
      }
    )
  }

  let footer = []
  if (aeFile) {
    footer = [
      <PrimeButton
        size="small"
        onClick={() => {
          setAeFile(null)
          setOpenAeImportModal(false)
        }}
      >
        Cancel
      </PrimeButton>,
      <PrimeButton
        type="dashed"
        size="small"
        onClick={() => {
          setAeFile(null)
        }}
      >
        Remove file
      </PrimeButton>,
      <PrimeButton
        size="small"
        onClick={handleImport}
        loading={loadingXmlImport}
        disabled={showAeImportErrors || loadingXmlImport}
      >
        Import
      </PrimeButton>,
    ]
  } else {
    footer = [
      <PrimeButton
        size="small"
        style={{
          cursor: "pointer",
        }}
        onClick={() => {
          setOpenAeImportModal(false)
          setShowAeImportErrors(false)
        }}
      >
        Cancel
      </PrimeButton>,
      <PrimeButton
        type="dashed"
        size="small"
        style={{
          cursor: "pointer",
        }}
        loading={loadingXmlImport}
        onClick={() => {}}
        disabled={true}
      >
        Import
      </PrimeButton>,
    ]
  }

  const classNames = ["results-table", customTableWidthClassName]

  return (
    <div className={classNames.join(" ")}>
      {formView.id !== "global" && readOnly !== true && (
        <PrimeButton
          icon={<i className="fal fa-cloud-upload"></i>}
          type="dashed"
          style={{ marginBottom: 15 }}
          onClick={() => setOpenAeImportModal(true)}
        >
          <span style={{ marginLeft: 6 }}>Import XML</span>
        </PrimeButton>
      )}

      <table
        className="authoring-table-component"
        style={{ marginBottom: 24, alignSelf: "flex-start" }}
      >
        <tbody>
          <AdverseProperties
            schema={schema}
            adverseData={adverseData}
            setAdverseData={setAdverseData}
          />
        </tbody>
      </table>
      <table style={{ alignSelf: "flex-start" }}>
        <tbody>
          <Arms
            schema={schema}
            adverseData={adverseData}
            setAdverseData={setAdverseData}
          />
          {tableView === "ctg" && (
            <>
              <tr>
                <td
                  className="disabled-row"
                  style={{ fontWeight: "bold" }}
                  colSpan={adverseData?.[tableView]?.groups?.length + 1}
                >
                  All Cause Mortality
                </td>
              </tr>
              <CTGArmTotal
                type="all_cause_mortality_group_total"
                schema={schema}
                adverseData={adverseData}
                setAdverseData={setAdverseData}
              />
            </>
          )}
          <EventList
            title="Serious adverse event"
            eventKey={"serious_adverse_event"}
            eventListKey={"serious_adverse_events"}
            schema={schema}
            adverseData={adverseData}
            setAdverseData={setAdverseData}
          />
          <EventList
            title="Non-serious adverse event"
            eventKey={"non_serious_adverse_event"}
            eventListKey={"non_serious_adverse_events"}
            schema={schema}
            adverseData={adverseData}
            setAdverseData={setAdverseData}
          />
        </tbody>
      </table>

      <CellValidation
        table="adverse"
        errorKey="results_adverse_events_adverse_events_table"
      />

      <Modal
        title="Adverse Events XML Import"
        onCancel={() => {
          setOpenAeImportModal(false)
          setAeFile(null)
        }}
        open={openAeImportModal}
        hideImage={true}
        className="ae-xml-upload-modal"
        footer={footer}
        width={500}
      >
        <AdverseEventsXMLImport
          aeFile={aeFile}
          setAeFile={setAeFile}
          openAeImportModal={openAeImportModal}
          handleFileload={handleFileload}
          showAeImportErrors={showAeImportErrors}
          setShowAeImportErrors={setShowAeImportErrors}
          aeImportError={aeImportError}
          setAeImportError={setAeImportError}
        />
      </Modal>
      <Modal
        open={showImportSuccess}
        title={"ETL load in process"}
        message={
          "The form is temporarily locked.  You will receive an email notification when upload is complete and form is unlocked."
        }
        footer={[
          <PrimeButton
            key="ok-btn"
            style={{
              margin: "auto",
              paddingLeft: 64,
              paddingRight: 64,
              marginTop: 24,
            }}
            onClick={() => {
              setShowImportSuccess(false)
              setAeFile(null)
              setIsUserLocked(true)
              setLockedFullName("ETL Load")
            }}
          >
            OK
          </PrimeButton>,
        ]}
      ></Modal>
    </div>
  )
}

export default RJAdverseEvents
